<script setup>
import { useStore } from 'vuex';
import AppMenu from './AppMenu.vue';
import AppAlertList from './AppAlertList.vue';
import { ref, watch, computed, onMounted, onUnmounted, onBeforeUnmount } from 'vue';
import { useRoute } from 'vue-router';

let socket = null; // ✅ WebSocket 인스턴스
let reconnectTimer = null; // 🔹 재연결 예약 타이머 (중복 예약 방지)
let reconnectAttempts = 0; // 🔹 연속 재연결 시도 횟수 (지수 백오프 계산)
let isManualClose = false; // 🔹 로그아웃/언마운트 등 "의도적" 종료 여부 — true면 재연결하지 않음
const MAX_RECONNECT_ATTEMPTS = 10; // 🔹 이 횟수 넘게 실패하면 재연결 중단(무한 재연결 폭주 차단)
const store = useStore();
const route = useRoute();
const alertListVisible = ref(false);
import axiosInstance, { baseWsURL } from '@/service/axiosInstance.js';
import { stateRole, stateUserRole } from '@/common/commonStatus'; // ✅ WebSocket 환경변수 가져오기

// 동일 시점(focus + click) 중복 호출을 막기 위한 간단한 가드
const ALERT_SYNC_COOLDOWN_MS = 400;
let alertSyncInFlight = null;
let lastAlertSyncAt = 0;

// ✅ 기존 알람 데이터 불러오기 (백엔드 API 호출)
const fetchInitialAlerts = async () => {
    // 비로그인 사용자(예: 공개 알림 링크 방문자)는 알람 조회 대상이 아니며,
    // 호출 시 서버가 401을 반환해 axios 인터셉터가 강제로 로그인 페이지로 리다이렉트시키는 문제가 있었음
    if (!store.state.user) {
        return null;
    }

    const now = Date.now();

    if (alertSyncInFlight) {
        return alertSyncInFlight;
    }

    if (now - lastAlertSyncAt < ALERT_SYNC_COOLDOWN_MS) {
        return null;
    }

    alertSyncInFlight = (async () => {
        try {
            const response = await axiosInstance.post('/api/web/v1/event/sseEventList',
                {},
                {
                    headers: { 'Content-Type': 'application/json' },
                    withCredentials: true
                }
            );

            if (response.data?.body?.alerts) {
                store.dispatch('setAlertList', response.data.body.alerts); // 📌 Vuex 스토어에 알람 저장
                store.dispatch('updateAlertCount', response.data.body.alerts.length); // 📌 Vuex 스토어에 알람 개수 저장
            }
        } catch (error) {
            console.error('❌ 초기 알람 불러오기 실패:', error);
        } finally {
            lastAlertSyncAt = Date.now();
            alertSyncInFlight = null;
        }
    })();

    return alertSyncInFlight;
};

// ✅ WebSocket 연결 함수 (앱 실행 시 WebSocket 유지)
const connectWebSocket = () => {
    // 로그인 상태가 아니면 연결하지 않는다 (로그아웃 후 호출 방어)
    if (!store.state.user) return;

    // 중복 연결 방지: 이미 연결(또는 연결중)인 소켓이 살아있으면 새로 만들지 않는다
    if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) {
        return;
    }

    isManualClose = false;
    socket = new WebSocket(`${baseWsURL}/ws/alerts`);

    socket.onopen = () => {
        reconnectAttempts = 0; // 연결 성공 → 백오프 초기화
        // ✅ WebSocket 연결 후, 유저 정보 전송
        if (store.state.user?.user_id) {
            socket.send(JSON.stringify({ type: 'subscribe', userId: store.state.user.user_id }));
        }
    };

    socket.onmessage = (event) => {
        try {
            const newAlerts = JSON.parse(event.data);

            newAlerts.forEach((alert) => {
                store.dispatch('addAlert', alert);
                store.dispatch('incrementAlertCount'); // 📌 알람 카운트 증가
            });
        } catch (error) {
            console.error('❌ 알람 데이터 파싱 오류:', error);
        }
    };

    socket.onclose = () => {
        /*console.log("⚠️ WebSocket 연결 종료됨");*/
        socket = null;
        scheduleReconnect(); // 🔹 사유를 확인한 뒤 재연결할지 결정
    };

    socket.onerror = (error) => {
        console.error('❌ WebSocket 오류 발생:', error);
        // 에러 뒤엔 보통 onclose가 이어지지만, 확실히 하기 위해 명시적으로 닫는다
        if (socket) socket.close();
    };
};

// 🔹 WebSocket 재연결 스케줄러
//   기존에는 close 사유와 무관하게 무조건 5초 뒤 재연결했다. 그래서 세션 만료(핸드셰이크 401)로
//   끊긴 경우에도 스테일 세션 쿠키로 영원히 재접속을 반복했고, 서버 로그에 /ws/alerts
//   "Redis 세션 역직렬화 실패"가 수초마다 무한 반복됐다.
//   이제는 ① 의도적 종료/비로그인 시 중단, ② 재연결 직전 세션을 확인해 만료면 로그인 유도 후 중단,
//   ③ 지수 백오프 + 최대 횟수 제한을 적용한다.
const scheduleReconnect = () => {
    // 의도적 종료(로그아웃/언마운트)거나 비로그인 상태면 재연결하지 않는다
    if (isManualClose || !store.state.user) return;

    // 중복 예약 방지
    if (reconnectTimer) return;

    // 최대 시도 횟수 초과 시 중단
    if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
        console.warn('⚠️ WebSocket 재연결 최대 횟수 초과 — 재연결을 중단합니다.');
        return;
    }

    // 지수 백오프: 5s → 10s → 20s … 최대 30s (기존 고정 5초 무한 재연결 대체)
    const delay = Math.min(5000 * 2 ** reconnectAttempts, 30000);
    reconnectAttempts += 1;

    reconnectTimer = setTimeout(async () => {
        reconnectTimer = null;

        // 재연결 직전 세션 유효성 확인.
        //   - 세션이 살아있으면(2xx) 일시적 네트워크 문제로 보고 재연결
        //   - 세션이 만료됐으면(401) 재접속해도 핸드셰이크가 또 401이므로 로그인 페이지로 유도하고
        //     재연결 루프를 끊는다. (axios 인터셉터는 /sessionCheck 401을 무한루프 방지로
        //     리다이렉트 대상에서 제외하므로, 여기서 직접 처리해야 한다)
        try {
            await axiosInstance.get('/api/web/v1/common/sessionCheck', { withCredentials: true });
        } catch (error) {
            if (error?.response?.status === 401) {
                console.warn('⚠️ 세션 만료로 WebSocket 재연결 중단 — 로그인 페이지로 이동합니다.');
                isManualClose = true;
                reconnectAttempts = 0;
                sessionStorage.setItem('preLoginRoute', window.location.pathname + window.location.search);
                store.dispatch('logout');
                window.location.href = '/auth/login';
                return;
            }
            // 401이 아닌 오류(네트워크 등)는 세션 상태를 단정할 수 없으므로 재연결을 계속 시도한다
        }

        connectWebSocket();
    }, delay);
};

watch(
    () => route.path,
    () => {
        onMenuMobileClose();
        onAlertListClose();
    }
);

const onMenuToggle = () => {
    // Vuex를 통해 메뉴 상태를 토글
    store.dispatch('toggleMenu');
};

const onPcCloseMenuClick = () => {
    if (!store.state.menuVisible) {
        store.dispatch('toggleMenu');
    }
};

const onMenuMobileToggle = () => {
    store.dispatch('toggleMenuMo');
    if (!store.state.menuMoVisible) {
        onAlertListClose();
    }
};

const onMenuMobileClose = () => {
    if (store.state.menuMoVisible) {
        store.dispatch('toggleMenuMo');
    }
};

const onAlertListToggle = () => {
    alertListVisible.value ? onAlertListClose() : onAlertListOpen();
};

const onAlertListOpen = () => {
    // 팝업은 즉시 열고, 서버 동기화는 백그라운드로 진행한다.
    alertListVisible.value = true;
    fetchInitialAlerts();
    store.dispatch('setAlertList', store.state.alertList); // ✅ Vuex에 저장된 알람 리스트 가져오기
};

const onAlertListClose = () => {
    alertListVisible.value = false;
};

// 사용자 이름을 가져오기 위해 computed 속성 추가
const userName = computed(() => {
    return store.state.user ? store.state.user.user_name : null;
});

const enterpriseName = computed(() => {
    return store.state.user ? store.state.user.user_enterprise : null;
});

const enterpriseRole = computed(() => {
    return store.state.user ? store.state.user.enterprise_role : null;
});

const user_role = computed(() => {
    return store.state.user ? store.state.user.user_role : null;
});

onMounted(async () => {
    await fetchInitialAlerts(); // ✅ 앱 실행 시 알람 개수 불러오기
    if (store.state.user) {
        connectWebSocket(); // ✅ 로그인된 사용자만 WebSocket 유지
    }
});

const handleWindowFocus = async () => {
    // 창 전환 후 돌아왔을 때도 서버 기준 알림 상태를 다시 반영한다.
    await fetchInitialAlerts();

    // 절전 해제·네트워크 복구 등으로 돌아왔을 때, 백오프 누적/최대횟수 초과로 끊겨 있던
    // WebSocket을 즉시 되살린다. (재연결을 영구 포기한 상태에서의 회복 경로)
    if (store.state.user && !isManualClose && (!socket || socket.readyState === WebSocket.CLOSED)) {
        if (reconnectTimer) {
            clearTimeout(reconnectTimer);
            reconnectTimer = null;
        }
        reconnectAttempts = 0; // 백오프/최대횟수 카운터 초기화 후 재시작
        connectWebSocket();
    }
};

onMounted(() => {
    window.addEventListener('focus', handleWindowFocus);
});

// ✅ 브라우저 닫힐 때 disconnect 메시지 보내기
const sendDisconnectMessage = () => {
    if (socket && store.state.user?.user_id) {
        socket.send(JSON.stringify({ type: 'disconnect', userId: store.state.user.user_id }));
    }
};

// ✅ 브라우저 닫힐 때 이벤트 제거
onBeforeUnmount(() => {
    window.removeEventListener('beforeunload', sendDisconnectMessage);
    window.removeEventListener('focus', handleWindowFocus);
    // 언마운트는 "의도적" 종료 → onclose가 재연결을 예약하지 못하게 막고 예약된 타이머도 정리한다
    isManualClose = true;
    if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
    }
    if (socket) {
        socket.close();
    }
});

onUnmounted(() => {
    isManualClose = true;
    if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
    }
    if (socket) {
        socket.close();
    }
});

// ✅ 외부 클릭 감지
const handleClickOutside = (event) => {
    const alertEl = document.querySelector('.alert-list-popup');
    const bellBtn = document.querySelector('.menu-user-btn');

    if (
        alertListVisible.value && // 리스트가 열려 있을 때만 처리
        alertEl &&
        !alertEl.contains(event.target) && // 리스트 바깥을 클릭했을 때
        bellBtn &&
        !bellBtn.contains(event.target) // 벨 버튼도 아니면
    ) {
        onAlertListClose();
    }
};

onMounted(() => {
    document.addEventListener('click', handleClickOutside);
});

onBeforeUnmount(() => {
    document.removeEventListener('click', handleClickOutside);
});
</script>

<template>
    <div class="app-layout" :class="[{ 'close-pc': !store.state.menuVisible }, { 'open-mo': store.state.menuMoVisible }]">
        <div class="app-header-mobile">
            <router-link class="menu-logo-link" to="/new/dashboard">
                <img class="menu-logo" src="@/assets/images/logo-white.svg" alt="symon" />
            </router-link>
            <div class="spacer"></div>
            <Button text class="icon" @click="onMenuMobileToggle">
                <i class="pi pi-bars"></i>
            </Button>
        </div>
        <div class="app-menu">
            <div class="menu-header-mo-wrap">
                <img class="menu-logo" src="@/assets/images/logo-white.svg" alt="symon" />
                <div class="spacer"></div>
                <Button text class="icon" @click="onMenuMobileToggle">
                    <i class="pi pi-times"></i>
                </Button>
            </div>
            <div class="menu-logo-wrap">
                <router-link class="menu-logo-link" to="/new/dashboard">
                    <img class="menu-logo" src="@/assets/images/logo-white.svg" alt="symon" />
                    <img class="menu-logo-symbol" src="@/assets/images/logo-symbol.svg" alt="symon" />
                </router-link>
            </div>
            <div class="menu-user-wrap">
                <div class="menu-user-name-wrap">
                    <span class="menu-user-name">{{ userName }}</span>
                    <span>님</span>
                    <Chip v-if="stateUserRole[user_role]" :label="stateUserRole[user_role].label" :class="['small', stateUserRole[user_role].class]"></Chip>
                </div>
                <div class="menu-user-company">
                    {{ enterpriseName }}
                    <Chip class="small" v-if="stateRole[enterpriseRole]" :class="stateRole[enterpriseRole].class">
                        <span>{{ stateRole[enterpriseRole].label }}</span>
                    </Chip>
                </div>
                <Button class="menu-user-btn icon" aria-label="경고확인" text @click="onAlertListToggle">
                    <i class="pi pi-bell"></i>
                    <Badge v-if="store.state.alertCount > 0">{{ store.state.alertCount }}</Badge>
                </Button>
            </div>
            <AppMenu @click="onPcCloseMenuClick"></AppMenu>
            <Button class="menu-toggle-btn" :class="{ 'close-pc': !store.state.menuVisible }" @click="onMenuToggle()">
                <span>목록 접기</span>
                <i class="pi pi-chevron-left"></i>
            </Button>
            <AppAlertList v-if="alertListVisible" @close="onAlertListClose"></AppAlertList>
        </div>
        <div class="app-container">
            <router-view />
        </div>
    </div>
</template>
